import { BRAND_CLASSES } from '@/constants/theme'
import { Loader2 } from 'lucide-preact'

interface FormActionsProps {
  /** Whether the form is currently submitting */
  isSubmitting: boolean
  
  /** Whether the save button should be disabled (e.g., form invalid or no changes) */
  isSaveDisabled: boolean
  
  /** Callback when cancel button is clicked */
  onCancel: () => void
  
  /** Optional: Custom save button text (defaults to "Save" or "Saving..." based on isSubmitting) */
  saveText?: string
  
  /** Optional: Custom cancel button text (defaults to "Cancel") */
  cancelText?: string
  
  /** Optional: Show a back button (for multi-step forms) */
  showBackButton?: boolean
  
  /** Optional: Back button click handler */
  onBack?: () => void
  
  /** Optional: Custom onClick handler for save button (for multi-step forms where button shouldn't submit) */
  onSaveClick?: () => void

  /** Optional: id of the form this submit button belongs to. Required when FormActions is rendered outside the <form> element. */
  formId?: string

  /** Optional: Custom className for the container */
  className?: string
  
  /** Optional: Additional buttons to show on the left side */
  leftActions?: preact.ComponentChildren
  
  /** Optional: Additional buttons to show before Save (on right side) */
  rightActions?: preact.ComponentChildren
}

/**
 * Standardized form action buttons (Save/Cancel) for modal forms
 * Provides consistent styling, loading states, and behavior across all forms
 * 
 * @example
 * // Basic usage
 * <FormActions
 *   isSubmitting={isSubmitting}
 *   isSaveDisabled={!isValid}
 *   onCancel={handleClose}
 * />
 * 
 * @example
 * // With custom text and back button
 * <FormActions
 *   isSubmitting={isSubmitting}
 *   isSaveDisabled={!canProceed}
 *   onCancel={handleClose}
 *   saveText="Create Campaign"
 *   showBackButton={step === 2}
 *   onBack={() => setStep(1)}
 * />
 */
export function FormActions({
  isSubmitting,
  isSaveDisabled,
  onCancel,
  saveText,
  cancelText = 'Cancel',
  showBackButton = false,
  onBack,
  onSaveClick,
  formId,
  className = '',
  leftActions,
  rightActions,
}: FormActionsProps) {
  const defaultSaveText = isSubmitting ? 'Saving...' : 'Save'
  const displaySaveText = saveText || defaultSaveText
  
  return (
    <div className={`flex items-center justify-between gap-3 p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 ${className}`}>
      {/* Left side actions */}
      <div className="flex items-center gap-3">
        {showBackButton && onBack && (
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
        )}
        {leftActions}
      </div>
      
      {/* Right side actions */}
      <div className="flex items-center gap-3 ml-auto">
        {rightActions}
        
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {cancelText}
        </button>
        
        <button
          type={onSaveClick ? 'button' : 'submit'}
          onClick={onSaveClick}
          form={onSaveClick ? undefined : formId}
          disabled={isSaveDisabled || isSubmitting}
          className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2 ${BRAND_CLASSES.BUTTON.PRIMARY} disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isSubmitting && (
            <Loader2 className="w-4 h-4 animate-spin" />
          )}
          {displaySaveText}
        </button>
      </div>
    </div>
  )
}
